<template>
    <div class="p-4">
        <ValidationObserver ref="programForm">
            <fieldset class="border p-3 mb-4 rounded bg-white shadow-sm">
                <legend class="w-auto px-2 font-weight-bold h6">{{ $t('applied_program_info') }}</legend>
                <b-row class="align-items-start">
                    <b-col xs="12" sm="4" class="mb-3">
                        <ValidationProvider name="programLevel" rules="required" v-slot="{errors}">
                            <b-form-group :label="$t('program_level')">
                                <parameter-selectbox
                                    ref="levelRef"
                                    code="program_levels"
                                    v-model="appliedProgram.level"
                                    :public="true"
                                    :validate-error="errors[0]"
                                />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>

                    <template v-if="isUndergradLayout">
                        <b-col xs="12" sm="4" class="mb-3">
                            <ValidationProvider name="applyFaculty" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('faculty')">
                                    <faculty-selectbox ref="facultyRef" v-model="appliedProgram.faculty" :validateError="errors[0]" :public="true"></faculty-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <b-col xs="12" sm="4" class="mb-3">
                            <ValidationProvider name="applyDepartment" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('department')">
                                    <department-selectbox
                                        v-model="appliedProgram.department"
                                        :is_faculty_code_required="true"
                                        :faculty_code="appliedProgram.faculty"
                                        :validateError="errors[0]"
                                        :public="true"
                                    ></department-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <b-col xs="12" sm="4" class="mb-3 offset-sm-4 offset-md-4">
                            <ValidationProvider name="applyProgram" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('program')">
                                    <program-selectbox
                                        ref="programRef"
                                        v-model="appliedProgram.program"
                                        :is_faculty_code_required="true"
                                        :faculty_code="appliedProgram.faculty"
                                        :is_department_code_required="true"
                                        :department_code="appliedProgram.department"
                                        :validateError="errors[0]"
                                        :public="true"
                                    ></program-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </template>

                    <template v-else-if="isGraduateLayout">
                        <b-col xs="12" sm="4" class="mb-3">
                            <ValidationProvider name="thesisType" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('with_without_thesis')">
                                    <ThesisWithTrueFalseSelectbox v-model="appliedProgram.thesis"
                                              :validateError="errors[0]"
                                />
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <b-col xs="12" sm="4" class="mb-3">
                            <ValidationProvider name="applyProgram" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('program')">
                                    <program-selectbox ref="programRef" v-model="appliedProgram.program" :validateError="errors[0]" :public="true"></program-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </template>
                </b-row>

                <div v-if="appliedProgram.level === 'H'" class="mt-2 text-info d-flex align-items-start">
                    <i class="ri-information-line ri-lg mr-2"></i>
                    <small class="font-italic">
                        {{ $t('guest_student_prep_faculty_warning') }}
                    </small>
                </div>
            </fieldset>

            <div class="d-flex justify-content-center mt-3">
                <b-button variant="primary" @click="submitAndNext" class="px-5 font-weight-bold shadow-sm" :disabled="loading">
                    <b-spinner small v-if="loading" class="mr-2" />
                    {{ $t('next') }}
                </b-button>
            </div>

        </ValidationObserver>
    </div>
</template>

<script>
import {ValidationObserver, ValidationProvider} from "vee-validate";
import FacultySelectbox from "@/components/interactive-fields/FacultySelectbox.vue";
import DepartmentSelectbox from "@/components/interactive-fields/DepartmentSelectbox.vue";
import ProgramSelectbox from "@/components/interactive-fields/ProgramSelectbox.vue";
import ThesisWithTrueFalseSelectbox from "@/components/interactive-fields/ThesisWithTrueFalseSelectbox.vue";
import ParameterSelectbox from "@/components/interactive-fields/ParameterSelectbox.vue";
import VisitorStudentApplicationServices from "@/services/VisitorStudentApplicationServices";

export default {
    name: "ProgramSelectionTab",
    components: {
        ValidationObserver,
        ValidationProvider,
        FacultySelectbox,
        DepartmentSelectbox,
        ProgramSelectbox,
        ThesisWithTrueFalseSelectbox,
        ParameterSelectbox
    },
    props: {
        userData: {
            type: Object,
            default: () => ({})
        },
        initialProgram: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        const saved = this.initialProgram?.appliedProgram || {};
        return {
            loading: false,
            appliedProgram: {
                faculty: saved.faculty || null,
                department: saved.department || null,
                program: saved.program || null,
                level: saved.level || null,
                thesis: saved.thesis || null
            }
        }
    },
    computed: {
        isUndergradLayout() {
            return ['OL', 'L', 'H'].includes(this.appliedProgram.level);
        },
        isGraduateLayout() {
            return ['YL', 'D'].includes(this.appliedProgram.level);
        }
    },
    methods: {
        async submitAndNext() {
            const isValid = await this.$refs.programForm.validate();
            if (!isValid) return;

            this.loading = true;
            try {
                const credentials = this.$store.getters['VisitorStudentApplicationStore/getCredentials'];

                const payload = {
                    ...credentials,
                    programs: [
                        {
                            program_level: this.appliedProgram.level,
                            faculty_code: this.appliedProgram.faculty || null,
                            department_id: this.appliedProgram.department || null,
                            program_code: this.appliedProgram.program || null,
                            graduate_type: this.appliedProgram.thesis || null
                        }
                    ]
                };

                const response = await VisitorStudentApplicationServices.updateProgramSelection(payload);
                const message = response.data?.message || response.data?.data?.message;
                if (message) this.$toast.success(this.$t('api.' + message));
                const facultyOptions = this.$refs.facultyRef?.options || [];
                const programOptions = this.$refs.programRef?.options || [];
                const levelOptions = this.$refs.levelRef?.options || [];
                const facultyText = facultyOptions.find(o => o.value === this.appliedProgram.faculty)?.text || this.appliedProgram.faculty;
                const programText = programOptions.find(o => o.value === this.appliedProgram.program)?.text || this.appliedProgram.program;
                const levelText = levelOptions.find(o => o.value === this.appliedProgram.level)?.text || this.appliedProgram.level;

                this.$emit('next', {
                    appliedProgram: {
                        ...this.appliedProgram,
                        faculty_name: facultyText,
                        program_name: programText,
                        level_name: levelText
                    }
                });
            } catch (error) {
                this.showErrors(error, this.$refs.programForm);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>
